import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import type { CustomerOrderBalance } from '../../types';
import CustomerPicker, { PickedCustomer } from '../../components/commercial/CustomerPicker';
import { formatDay, formatMoney } from '../../components/commercial/contact';

const METHODS = ['Efectivo', 'Transferencia', 'Mercado Pago', 'Cheque'];

const MobileCollectPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [pending, setPending] = useState<CustomerOrderBalance[]>([]);
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('Transferencia');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ amount: number; name: string } | null>(null);

  // Viene de "Hoy → Registrar cobro" con ?customerId=
  useEffect(() => {
    const id = Number(params.get('customerId'));
    if (!id) return;
    (async () => {
      try {
        const c = await api.getCustomer(id);
        setCustomer({ id: c.id, name: c.displayName || c.legalName, phone: c.phone, whatsApp: c.whatsApp });
      } catch { /* link viejo: se elige a mano */ }
    })();
  }, [params]);

  useEffect(() => {
    if (!customer) { setPending([]); setBalance(null); return; }
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [summary, orders] = await Promise.all([
          api.getCustomerAccountSummary(customer.id),
          api.getCustomerPendingOrders(customer.id),
        ]);
        setPending(orders);
        const due = orders.reduce((s, o) => s + o.remainingBalance, 0);
        setBalance(due || summary.currentBalance);
        setAmount(due > 0 ? String(Math.round(due * 100) / 100) : '');
      } catch (e: any) {
        setError(e?.response?.data?.message || 'No se pudo cargar la cuenta del cliente');
      } finally {
        setLoading(false);
      }
    })();
  }, [customer]);

  const submit = async () => {
    const value = Number(amount);
    if (!customer) { setError('Elegí el cliente'); return; }
    if (!value || value <= 0) { setError('Ingresá el monto'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.registerCustomerPayment(customer.id, { amount: value, paymentMethod: method });
      setDone({ amount: value, name: customer.name });
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo registrar el cobro');
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <Stack spacing={2} alignItems="center" sx={{ pt: 4, textAlign: 'center' }}>
        <CheckCircle color="success" sx={{ fontSize: 80 }} />
        <Typography variant="h5" fontWeight={800}>Cobro registrado</Typography>
        <Typography variant="h4" fontWeight={800}>{formatMoney(done.amount)}</Typography>
        <Typography color="text.secondary">{done.name} · {method}</Typography>
        <Button variant="contained" size="large" sx={{ minHeight: 56, width: '100%' }}
          onClick={() => { setDone(null); setCustomer(null); setAmount(''); }}>
          Otro cobro
        </Button>
        <Button variant="outlined" size="large" sx={{ minHeight: 56, width: '100%' }} onClick={() => navigate('/m')}>Volver al inicio</Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <CustomerPicker value={customer} onChange={setCustomer} />

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress /></Box>}

      {customer && !loading && balance != null && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary">Debe</Typography>
            <Typography variant="h4" fontWeight={800} color={balance > 0 ? 'error.main' : 'success.main'}>{formatMoney(balance)}</Typography>
            {pending.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {pending.slice(0, 6).map(o => (
                  <Stack key={o.orderId} direction="row" justifyContent="space-between">
                    <Typography variant="body2">
                      {o.reference} · {formatDay(o.orderDate)}
                      {o.daysOverdue && o.daysOverdue > 0 ? ` · vencida ${o.daysOverdue} d` : ''}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>{formatMoney(o.remainingBalance)}</Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {customer && (
        <>
          <TextField
            label="Monto cobrado"
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            inputProps={{ inputMode: 'decimal' }}
            InputProps={{ sx: { fontSize: 24, fontWeight: 700, minHeight: 64 } }}
          />
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {METHODS.map(m => (
              <Chip key={m} label={m} onClick={() => setMethod(m)} color={method === m ? 'primary' : 'default'}
                variant={method === m ? 'filled' : 'outlined'} sx={{ fontSize: 15, height: 40 }} />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Se imputa a las ventas más viejas primero.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <Button variant="contained" color="success" size="large" onClick={submit} disabled={saving}
            sx={{ minHeight: 60, fontSize: 20, fontWeight: 800 }}>
            {saving ? 'Guardando…' : `Registrar ${amount ? formatMoney(Number(amount)) : 'cobro'}`}
          </Button>
        </>
      )}
      {!customer && error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
};

export default MobileCollectPage;
